/**
 * view_item_list — product impressions on listing surfaces. (#156)
 *
 * Reports products that are ACTUALLY SEEN, not products that exist. A 24-product grid with 4 cards
 * above the fold reports 4 impressions, not 24 — otherwise the click-through-rate denominator is
 * fiction and "this product underperforms" can't be told apart from "this product is never visible".
 *
 * Contract, emitted by Liquid:
 *   container  [data-gtm-list-id]      required — e.g. "collection_fresh-fruits"
 *              [data-gtm-list-name]    required — e.g. "Fresh Fruits"
 *              [data-gtm-list-offset]  optional — paginate.current_offset, so page 2 starts at 25
 *   card       [data-gtm-item]         required — one GA4 item object, HTML-escaped JSON,
 *                                       built by snippets/analytics-item.liquid
 *
 * Position is computed here from DOM order rather than stamped by Liquid, because `index` must be
 * the true 1-based position after filtering, sorting and load-more — all of which reorder or extend
 * the list after render.
 *
 * No IntersectionObserver (very old browsers) => this does nothing. That is deliberate: reporting
 * every card as seen would be worse than reporting none, because it silently inflates the
 * denominator rather than leaving an obvious gap.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  window.dataLayer = window.dataLayer || [];

  var VISIBLE_RATIO = 0.5;   // half the card in view counts as seen
  var FLUSH_MS = 500;        // batch window — one event per burst of scrolling, not one per card
  var FLUSH_SIZE = 10;       // ...or as soon as this many are queued

  // listId -> { name, seen:Set<item_id>, queue:[], timer:number|null }
  var lists = Object.create(null);

  function listState(id, name) {
    if (!lists[id]) lists[id] = { name: name, seen: Object.create(null), queue: [], timer: null };
    return lists[id];
  }

  function flush(listId) {
    var st = lists[listId];
    if (!st || !st.queue.length) return;

    var items = st.queue.slice();
    st.queue.length = 0;
    if (st.timer) { clearTimeout(st.timer); st.timer = null; }

    items.sort(function (a, b) { return a.index - b.index; });

    // Clear the previous ecommerce object first. GA4 merges dataLayer state, so without this a
    // later event can inherit items[] from this one.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: { item_list_id: listId, item_list_name: st.name, items: items }
    });
  }

  function scheduleFlush(listId) {
    var st = lists[listId];
    if (!st) return;
    if (st.queue.length >= FLUSH_SIZE) { flush(listId); return; }
    if (st.timer) return;
    st.timer = setTimeout(function () { st.timer = null; flush(listId); }, FLUSH_MS);
  }

  function matches(el, selector) {
    var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return fn ? fn.call(el, selector) : false;
  }

  /**
   * True 1-based position among the cards in this container, plus any pagination offset.
   *
   * 🔴 Must stay identical to `positionOf` in `assets/analytics-select-item.js`. view_item_list and
   * select_item are only joinable if a card's `index` is the same in both, and this theme has no
   * bundler to share a module through. The test suite asserts both files agree on the same DOM.
   *
   * Swiper 8 runs the homepage carousels with `loop: true`, which CLONES slides into the same
   * `.swiper-wrapper` — `data-gtm-item` and all. Clones are excluded from the count, because Swiper
   * prepends them and every index after that would be shifted. A clone that scrolls into view is
   * still a real impression of a real product, so it resolves to the original's position by matching
   * the payload it was copied from. (#157)
   */
  function positionOf(container, el) {
    var all = container.querySelectorAll('[data-gtm-item]');
    var offset = parseInt(container.getAttribute('data-gtm-list-offset'), 10);
    if (isNaN(offset) || offset < 0) offset = 0;

    var real = [];
    for (var i = 0; i < all.length; i++) {
      if (!matches(all[i], '.swiper-slide-duplicate')) real.push(all[i]);
    }

    for (var j = 0; j < real.length; j++) {
      if (real[j] === el) return offset + j + 1;
    }

    var payload = el.getAttribute('data-gtm-item');
    for (var k = 0; k < real.length; k++) {
      if (real[k].getAttribute('data-gtm-item') === payload) return offset + k + 1;
    }
    return offset + 1;
  }

  var observer = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.isIntersecting) continue;

      var el = entry.target;
      var container = el.closest('[data-gtm-list-id]');
      if (!container) { observer.unobserve(el); continue; }

      // Stop watching immediately — an impression is reported once, and scrolling a card back into
      // view is not a second impression.
      observer.unobserve(el);

      var item;
      try {
        item = JSON.parse(el.getAttribute('data-gtm-item'));
      } catch (e) {
        continue; // a malformed payload on one card must not stop the rest of the list reporting
      }
      if (!item || !item.item_id) continue;

      var listId = container.getAttribute('data-gtm-list-id');
      var listName = container.getAttribute('data-gtm-list-name') || '';
      var st = listState(listId, listName);

      // Dedup per list. Survives filter/sort re-renders on purpose: a product that was already
      // reported and merely moved is not a new impression. Load-more adds genuinely new cards,
      // which aren't in `seen`, so those do report.
      if (st.seen[item.item_id]) continue;
      st.seen[item.item_id] = true;

      item.index = positionOf(container, el);
      item.item_list_id = listId;
      item.item_list_name = listName;

      st.queue.push(item);
      scheduleFlush(listId);
    }
  }, { threshold: VISIBLE_RATIO });

  function observeAll(root) {
    var containers = (root || document).querySelectorAll('[data-gtm-list-id]');
    for (var i = 0; i < containers.length; i++) {
      var cards = containers[i].querySelectorAll('[data-gtm-item]');
      for (var j = 0; j < cards.length; j++) {
        if (cards[j].getAttribute('data-gtm-observed')) continue;
        cards[j].setAttribute('data-gtm-observed', '1');
        observer.observe(cards[j]);
      }
    }
  }

  function start() {
    observeAll(document);

    // Filters, sorting, load-more and quick-view all replace or extend list markup after load.
    // Watching the tree means new cards are picked up without every one of those features having
    // to know this file exists.
    if ('MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; observeAll(document); }, 100);
      }).observe(document.body, { childList: true, subtree: true });
    }

    // Anything still queued when the page goes away would otherwise be lost.
    window.addEventListener('pagehide', function () {
      for (var id in lists) flush(id);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
